/**
 * Gabarits de mise en page et recettes de design livrés par le Core (ADR-146 §3.2, §3.4 ; 148 §2).
 * Données génériques, sans terme métier : un Pack ou un client ajoute les siens dans sa couche, il ne
 * modifie jamais ceux-ci (ADR-115). Libellés, descriptions et vignettes sont en langage courant : l'éditeur
 * les montre à des personnes qui ne sont pas informaticiennes (règle de l'utilisateur du 2026-10-19).
 */
const t = (fr, en) => ({ fr, en });
export const DEFAULT_LAYOUT_ID = 'core.layout.single_column';
export const CORE_LAYOUTS = [
    {
        artifact_id: 'core.layout.single_column',
        body: {
            label: t('Une colonne', 'Single column'),
            description: t('Tout le formulaire sur une colonne, de haut en bas. Pour un contrôle court.', 'The whole form in one column, top to bottom. For a short check.'),
            surfaces: ['operator', 'web'],
            regions: [
                { region_id: 'header', role: 'header', label: t('En-tête', 'Header'), accepts: ['display', 'summary', 'navigation'] },
                { region_id: 'full', role: 'main', label: t('Contenu', 'Content'), accepts: ['section', 'field', 'display', 'decision', 'capture', 'summary'] },
                { region_id: 'footer', role: 'footer', label: t('Pied', 'Footer'), accepts: ['navigation', 'display'] },
            ],
            navigation: { allowed: ['single', 'tabs', 'wizard'], default: 'single' },
            footer: { start: 'previous', center: 'progress', end: 'next' },
            drop_region: 'full',
            preview: { columns: 1, rows: 3, cells: { header: { column: 1, row: 1 }, full: { column: 1, row: 2 }, footer: { column: 1, row: 3 } } },
        },
    },
    {
        artifact_id: 'core.layout.operator_form',
        body: {
            label: t('Formulaire opérateur', 'Operator form'),
            description: t('Un en-tête, deux colonnes et une zone pleine largeur. Pour une procédure à suivre dans l’ordre.', 'A header, two columns and a full-width area. For a procedure followed in order.'),
            surfaces: ['operator', 'web'],
            regions: [
                { region_id: 'header', role: 'header', label: t('En-tête', 'Header'), accepts: ['display', 'summary', 'navigation'] },
                { region_id: 'left', role: 'main', label: t('Colonne de gauche', 'Left column'), accepts: ['section', 'field', 'display', 'capture'], width: 7 },
                { region_id: 'right', role: 'side', label: t('Colonne de droite', 'Right column'), accepts: ['section', 'display', 'summary', 'decision'], width: 5 },
                { region_id: 'full', role: 'main', label: t('Pleine largeur', 'Full width'), accepts: ['section', 'field', 'display', 'decision', 'capture', 'summary'] },
                { region_id: 'footer', role: 'footer', label: t('Pied', 'Footer'), accepts: ['navigation', 'display'] },
            ],
            formats: { portrait: { order: ['header', 'left', 'right', 'full', 'footer'], stack: true }, mobile: { stack: true } },
            navigation: { allowed: ['single', 'tabs', 'wizard'], default: 'wizard' },
            footer: { start: 'previous', center: 'progress', end: 'next' },
            drop_region: 'full',
            preview: { columns: 2, rows: 4, cells: { header: { column: 1, row: 1, column_span: 2 }, left: { column: 1, row: 2 }, right: { column: 2, row: 2 }, full: { column: 1, row: 3, column_span: 2 }, footer: { column: 1, row: 4, column_span: 2 } } },
        },
    },
    {
        artifact_id: 'core.layout.master_detail',
        body: {
            label: t('Liste et détail', 'Master and detail'),
            description: t('Une liste à gauche, le détail de l’élément choisi à droite. Pour une fiche à consulter.', 'A list on the left, the chosen item on the right. For a record to look up.'),
            surfaces: ['operator', 'web'],
            regions: [
                { region_id: 'header', role: 'header', label: t('En-tête', 'Header'), accepts: ['display', 'summary', 'navigation'] },
                { region_id: 'list', role: 'list', label: t('Liste', 'List'), accepts: ['section', 'display'], width: 5 },
                { region_id: 'detail', role: 'detail', label: t('Détail', 'Detail'), accepts: ['section', 'field', 'display', 'decision', 'capture'], width: 7 },
                { region_id: 'footer', role: 'footer', label: t('Pied', 'Footer'), accepts: ['navigation', 'display'] },
            ],
            formats: { portrait: { stack: true }, mobile: { stack: true } },
            navigation: { allowed: ['tabs', 'single', 'wizard'], default: 'tabs' },
            footer: { start: 'previous', center: 'progress', end: 'next' },
            drop_region: 'detail',
            preview: { columns: 2, rows: 3, cells: { header: { column: 1, row: 1, column_span: 2 }, list: { column: 1, row: 2 }, detail: { column: 2, row: 2 }, footer: { column: 1, row: 3, column_span: 2 } } },
        },
    },
    {
        artifact_id: 'core.layout.series_with_summary',
        body: {
            label: t('Saisie en série avec synthèse', 'Series entry with summary'),
            description: t('Une synthèse toujours visible, un tableau des lignes et un panneau de saisie. Pour une longue saisie, ligne après ligne.', 'An always-visible summary, a table of rows and an entry panel. For a long entry, row after row.'),
            surfaces: ['operator', 'web'],
            regions: [
                { region_id: 'header', role: 'header', label: t('En-tête', 'Header'), accepts: ['display', 'navigation'] },
                { region_id: 'summary', role: 'summary', label: t('Synthèse', 'Summary'), accepts: ['summary', 'display', 'decision'], persistent: true, width: 3, max_blocks: 3 },
                { region_id: 'main', role: 'main', label: t('Contenu', 'Content'), accepts: ['section', 'field', 'display', 'capture'], width: 6 },
                { region_id: 'entry', role: 'entry', label: t('Panneau de saisie', 'Entry panel'), accepts: ['capture', 'field'], width: 3 },
                { region_id: 'footer', role: 'footer', label: t('Pied', 'Footer'), accepts: ['navigation', 'display'] },
            ],
            formats: {
                landscape: { order: ['header', 'summary', 'main', 'entry', 'footer'] },
                portrait: { order: ['header', 'summary', 'main', 'entry', 'footer'], bands: { summary: 'top', entry: 'bottom' } },
                mobile: { stack: true, bands: { summary: 'top' } },
            },
            navigation: { allowed: ['steps_with_summary', 'wizard'], default: 'steps_with_summary' },
            footer: { start: 'previous', center: 'progress', end: 'next' },
            drop_region: 'main',
            preview: { columns: 3, rows: 2, cells: { header: { column: 1, row: 1, column_span: 3 }, summary: { column: 1, row: 2 }, main: { column: 2, row: 2 }, entry: { column: 3, row: 2 } } },
        },
    },
    {
        artifact_id: 'core.layout.inspection_form',
        body: {
            label: t('Inspection', 'Inspection'),
            description: t('Les points à contrôler à gauche, la décision à droite. Pour une inspection ou une tournée.', 'The points to check on the left, the decision on the right. For an inspection or a round.'),
            surfaces: ['operator', 'web'],
            regions: [
                { region_id: 'header', role: 'header', label: t('En-tête', 'Header'), accepts: ['display', 'summary', 'navigation'] },
                { region_id: 'full', role: 'main', label: t('Points de contrôle', 'Checkpoints'), accepts: ['section', 'field', 'display', 'capture'], width: 8 },
                { region_id: 'side', role: 'side', label: t('Décision', 'Decision'), accepts: ['section', 'decision', 'summary', 'display'], width: 4 },
                { region_id: 'footer', role: 'footer', label: t('Pied', 'Footer'), accepts: ['navigation', 'display'] },
            ],
            formats: { portrait: { stack: true }, mobile: { stack: true } },
            navigation: { allowed: ['single', 'tabs', 'wizard'], default: 'single' },
            footer: { start: 'previous', center: 'progress', end: 'next' },
            drop_region: 'full',
            preview: { columns: 3, rows: 3, cells: { header: { column: 1, row: 1, column_span: 3 }, full: { column: 1, row: 2, column_span: 2 }, side: { column: 3, row: 2 }, footer: { column: 1, row: 3, column_span: 3 } } },
        },
    },
    {
        artifact_id: 'core.layout.readings_board',
        body: {
            label: t('Relevés par zone', 'Readings by area'),
            description: t('Les zones et leurs points à gauche, la mesure et le pavé à droite. Pour un relevé de températures ou de compteurs.', 'Areas and their points on the left, the reading and keypad on the right. For temperature or meter readings.'),
            surfaces: ['operator', 'web'],
            regions: [
                { region_id: 'header', role: 'header', label: t('En-tête', 'Header'), accepts: ['display', 'summary', 'navigation'] },
                { region_id: 'board', role: 'board', label: t('Zones et points', 'Areas and points'), accepts: ['section', 'display'], width: 7 },
                { region_id: 'entry', role: 'entry', label: t('Mesure', 'Reading'), accepts: ['capture', 'field', 'display'], width: 5 },
                { region_id: 'footer', role: 'footer', label: t('Pied', 'Footer'), accepts: ['navigation', 'display'] },
            ],
            formats: { portrait: { bands: { entry: 'bottom' } }, mobile: { stack: true } },
            navigation: { allowed: ['single', 'wizard'], default: 'single' },
            footer: { start: 'previous', center: 'progress', end: 'next' },
            drop_region: 'board',
            preview: { columns: 2, rows: 3, cells: { header: { column: 1, row: 1, column_span: 2 }, board: { column: 1, row: 2 }, entry: { column: 2, row: 2 }, footer: { column: 1, row: 3, column_span: 2 } } },
        },
    },
    {
        // ADR-146 addendum (16) du 2026-10-21 (layout-body 1.1.0) : bandeau, bande de champs compacts, liste
        // et détail de la ligne choisie, sur un seul écran ; la disposition du formulaire tablette du v1.
        artifact_id: 'core.layout.strip_list_detail',
        body: {
            label: t('Bandeau, bande de champs, liste et détail', 'Header, field strip, list and detail'),
            description: t('Une rangée de champs compacts sous le bandeau, puis la liste des lignes à gauche et le détail de la ligne choisie à droite. Pour une saisie de lignes sur un seul écran.', 'A row of compact fields under the header, then the list of rows on the left and the chosen row on the right. For entering rows on a single screen.'),
            surfaces: ['operator', 'web'],
            regions: [
                { region_id: 'header', role: 'header', label: t('Bandeau', 'Header'), accepts: ['display', 'summary', 'navigation'] },
                { region_id: 'strip', role: 'strip', label: t('Bande de champs', 'Field strip'), accepts: ['section', 'field', 'capture'] },
                { region_id: 'list', role: 'list', label: t('Liste', 'List'), accepts: ['section', 'summary', 'display'], width: 7 },
                { region_id: 'detail', role: 'detail', label: t('Détail', 'Detail'), accepts: ['section', 'field', 'capture', 'display', 'decision'], width: 5 },
                { region_id: 'footer', role: 'footer', label: t('Barre du bas', 'Bottom bar'), accepts: ['navigation', 'display'] },
            ],
            formats: {
                landscape: { order: ['header', 'strip', 'list', 'detail', 'footer'] },
                portrait: { order: ['header', 'strip', 'list', 'detail', 'footer'], bands: { detail: 'bottom' } },
                mobile: { stack: true },
            },
            navigation: { allowed: ['single', 'tabs', 'wizard'], default: 'single' },
            footer: { start: 'none', center: 'progress', end: 'submit' },
            drop_region: 'detail',
            preview: { columns: 3, rows: 4, cells: { header: { column: 1, row: 1, column_span: 3 }, strip: { column: 1, row: 2, column_span: 3 }, list: { column: 1, row: 3, column_span: 2 }, detail: { column: 3, row: 3 }, footer: { column: 1, row: 4, column_span: 3 } } },
        },
    },
];
const ORDER_MESSAGE = t('Ce formulaire impose un ordre : les onglets ne conviennent pas. Choisissez « Assistant » ou « Étapes avec synthèse ».', 'This form follows a set order: tabs are not suitable. Choose “Wizard” or “Steps with summary”.');
const SUGGESTIONS = [
    { intent: 'series_entry', layout_ref: 'core.layout.series_with_summary', navigation: 'steps_with_summary' },
    { intent: 'short_check', layout_ref: 'core.layout.single_column', navigation: 'single' },
    { intent: 'ordered_procedure', layout_ref: 'core.layout.operator_form', navigation: 'wizard' },
    { intent: 'record_view', layout_ref: 'core.layout.master_detail', navigation: 'tabs' },
];
export const CORE_RECIPES = [
    {
        artifact_id: 'core.recipe.back_office_form',
        body: {
            label: t('Bureau', 'Back office'),
            description: t('Saisie au bureau, avec un clavier et une souris.', 'Entry at a desk, with a keyboard and a mouse.'),
            applies_to: { surfaces: ['web'], default_for_surface: true },
            constraints: { forbidden_navigation_by_intent: { ordered_procedure: ['tabs'] } },
            suggestions: SUGGESTIONS,
            messages: { RECIPE_NAVIGATION_FORBIDDEN_FOR_INTENT: ORDER_MESSAGE },
        },
    },
    {
        artifact_id: 'core.recipe.operator_gloves',
        body: {
            label: t('Atelier avec des gants', 'Workshop with gloves'),
            description: t('Tablette d’atelier utilisée avec des gants : grandes cibles, pas de clavier.', 'Workshop tablet used with gloves: large targets, no keyboard.'),
            applies_to: { surfaces: ['operator'], environments: ['gloves'], default_for_surface: true },
            constraints: { min_target_px: 56, density: 'gloves', forbidden_block_inputs: ['keyboard'], forbidden_navigation_by_intent: { ordered_procedure: ['tabs'] } },
            suggestions: SUGGESTIONS,
            messages: {
                RECIPE_NAVIGATION_FORBIDDEN_FOR_INTENT: ORDER_MESSAGE,
                RECIPE_BLOCK_INPUT_FORBIDDEN: t('Ce bloc demande un clavier : il n’est pas adapté à une saisie avec des gants. Choisissez un bloc au pavé ou au toucher.', 'This block needs a keyboard: it is not suitable for entry with gloves. Choose a keypad or touch block.'),
            },
        },
    },
    {
        artifact_id: 'core.recipe.warehouse_fast_entry',
        body: {
            label: t('Entrepôt, saisie rapide', 'Warehouse, fast entry'),
            description: t('Saisie rapide en entrepôt, souvent à la douchette : on scanne d’abord, on valide en bas à droite.', 'Fast warehouse entry, often with a scanner: scan first, confirm at the bottom right.'),
            applies_to: { surfaces: ['operator'], environments: ['scanner', 'frequent'] },
            constraints: { min_target_px: 48, forbidden_block_inputs: ['keyboard'], forbidden_navigation: ['tabs'], forbidden_navigation_by_intent: { ordered_procedure: ['tabs'] } },
            // Sans onglets : une fiche à consulter passe en assistant.
            suggestions: SUGGESTIONS.map((suggestion) => (suggestion.navigation === 'tabs' ? { ...suggestion, navigation: 'wizard' } : suggestion)),
            messages: {
                RECIPE_NAVIGATION_FORBIDDEN: t('Les onglets ralentissent une saisie rapide : choisissez « Page unique » ou « Assistant ».', 'Tabs slow down fast entry: choose “Single page” or “Wizard”.'),
                RECIPE_NAVIGATION_FORBIDDEN_FOR_INTENT: ORDER_MESSAGE,
                RECIPE_BLOCK_INPUT_FORBIDDEN: t('Ce bloc demande un clavier : il ralentit une saisie rapide. Choisissez un bloc au scan ou au pavé.', 'This block needs a keyboard: it slows down fast entry. Choose a scan or keypad block.'),
            },
        },
    },
];
//# sourceMappingURL=core-layouts.js.map